import { isIsolatedCloud, isolatedCloudDomain } from '@atlassian/atl-context';
import { ProductEnvironment, type ExtensionViewData } from '@atlassian/forge-ui-types';
import { fg } from '@atlaskit/platform-feature-flags';

import type { Extension } from '../../../web-client';
import { getForgeCDNBaseDomain } from '../../../utils';
import { getEncodedAppContext } from './getEncodedAppContext';

const SAFE_RESOURCE_ENTRY_PATTERN = /^[\w][\w.-]*$/;

/**
 * Appends `resolvedResourceEntry` to the base CDN URL after validating it is a
 * safe resource entry file name. Throws if the value is present but invalid.
 */
const appendResolvedResourceEntry = (
	baseUrl: string,
	resolvedResourceEntry: string | undefined,
): string => {
	if (resolvedResourceEntry === undefined) {
		return baseUrl;
	}
	if (!SAFE_RESOURCE_ENTRY_PATTERN.test(resolvedResourceEntry)) {
		throw new Error('Invalid resource entry specified.');
	}
	return `${baseUrl}${resolvedResourceEntry}`;
};

export const productEnvToCdnEnv: {
	DEVELOPMENT: string;
	PRODUCTION: string;
	STAGING: string;
} = {
	[ProductEnvironment.PRODUCTION]: 'prod',
	[ProductEnvironment.STAGING]: 'stg',
	[ProductEnvironment.DEVELOPMENT]: 'stg',
};

export const createSrcFromExtension = (
	appId: string,
	extension: Extension,
	environment: ProductEnvironment,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
): string => {
	if (fg('forge-ui-oasis-enabled')) {
		return isIsolatedCloud()
			? createSrcFromExtensionForIC(appId, extension, entryPoint, extensionViewData)
			: createSrcFromExtensionForCommercialAndFedRamp(
					appId,
					extension,
					environment,
					entryPoint,
					extensionViewData,
				);
	}
	return createSrcFromExtensionForCommercialAndFedRamp(
		appId,
		extension,
		environment,
		entryPoint,
		extensionViewData,
	);
};

export const createSrcFromExtensionForCommercialAndFedRamp = (
	appId: string,
	extension: Extension,
	environment: ProductEnvironment,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
): string => {
	const { installationId, environmentId, appVersion, properties } = extension;
	const resourceUploadId = properties.resourceUploadId || appVersion;

	const domain = `${shortenUuid(installationId)}--${shortenUuid(appId)}.${getForgeCDNBaseDomain(
		environment,
	)}`;

	/**
	 * The appId is also included in the subdomain so that it can be compared against the appId in the
	 * resource path. This comparison is performed by the routing Lambda@ Edge to prevent a malicious
	 * developer from appending their malicious app path to a legitimate installation id.
	 */
	const encodedAppContext = getEncodedAppContext(extension, extensionViewData);

	const urlSafeContext = urlSafeB64(encodedAppContext);

	const baseUrl = `https://${domain}/${appId}/${environmentId}/${resourceUploadId}/${entryPoint}/_ctx_${urlSafeContext}/`;
	return appendResolvedResourceEntry(
		baseUrl,
		fg('forge-ui-multi-entry-resource') ? properties.resolvedResourceEntry : undefined,
	);
};

export const createSrcFromExtensionForIC = (
	appId: string,
	extension: Extension,
	entryPoint: string,
	extensionViewData?: ExtensionViewData,
): string => {
	const { installationId, environmentId, appVersion, properties } = extension;
	const resourceUploadId = properties.resourceUploadId || appVersion;
	const icDomain = isolatedCloudDomain();

	const domain = `${shortenUuid(installationId)}--${shortenUuid(appId)}.forge-cdn.${icDomain}`;

	const encodedAppContext = getEncodedAppContext(extension, extensionViewData);

	const urlSafeContext = urlSafeB64(encodedAppContext);

	const baseUrl = `https://${domain}/forge-cdn/${appId}/${environmentId}/${resourceUploadId}/${entryPoint}/_ctx_${urlSafeContext}/`;
	return appendResolvedResourceEntry(
		baseUrl,
		fg('forge-ui-multi-entry-resource') ? properties.resolvedResourceEntry : undefined,
	);
};

/*
	UUIDs are in the format of 32 hexadecimal characters with four hyphens:
	XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX.

	If we intend to concatenate two shortened UUIDs in a subdomain label in the format
	${shortenedInstallationId}--${shortenedAppId}, where the max length of the label is
	63 characters (https://www.rfc-editor.org/rfc/rfc2181), then each id can have a max
	length of 30 characters. An underscore cannot be used as the delimiter because
	underscores are not allowed in domains. A single hyphen cannot be used because the
	delimiter needs to be unique and the ids themselves contain hyphens. Hence, the
	delimiter is a double-hyphen.

	The "highest" UUID is ffffffff-ffff-ffff-ffff-ffffffffffff. The base-36 string encoding
	(a-z, 0-9) is 1z141z3-1ekf-1ekf-1ekf-2rrvthnxtr, which has a length of 33. Too long.

	Combining the middle three segments in the UUID, i.e. ffffffff-ffffffffffff-ffffffffffff, and
	then encoding gives 1z141z3-2rrvthnxtr-2rrvthnxtr, which has a length of 29. This is what
	we will do.

	Precision should not be lost when decoding back to hexadecimal, because the longest segment
	in a UUID is 12 characters, while Number.MAX_SAFE_INTEGER.toString(16).length === 14.
*/
export function shortenUuid(uuid: string): string {
	const [a, b, c, d, e] = uuid.split('-');
	const combinedUuid = [a, `${b}${c}${d}`, e];

	return combinedUuid
		.map((s) => parseInt(s, 16))
		.map((s) => s.toString(36))
		.join('-');
}

export function expandUuid(shortenedUuid: string): string {
	const [a, b, c] = shortenedUuid
		.split('-')
		.map((s) => parseInt(s, 36))
		.map((s) => s.toString(16));

	// expands the middle segment into the original UUID
	// ffffffff-abcdabcdabcd-ffffffffffff ===> ffffffff-abcd-abcd-abcd-ffffffffffff
	const middleExpanded = b.match(/.{1,4}/g) || b;
	return [a, ...middleExpanded, c].join('-');
}

export function urlSafeB64(str: string): string {
	return str
		.replace(/\+/g, '-') // Replace '+' with '-'
		.replace(/\//g, '_') // Replace '/' with '_'
		.replace(/=+$/, ''); // Remove padding
}
